import { FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { startOfDay } from 'date-fns';

import { FormProperty } from '../../models/form.models';
import { FormsService, minArrayLength } from '../../services/forms.service';
import { NomenclatureService } from '../../services/nomenclature.service';
import { UtilsService } from '../../services/utils.service';
import { NomScheduleTypes, SCHEDULE_DAY_OF_MONTH_LAST } from '../../constants';
import { NomScheduleMisfirePolicy } from '../../models/schedule.models';

/**
 * UI-only schedule type extending NomScheduleTypes with the engine's advanced cron mode.
 * Not a nomenclature value - it maps to RecurrenceDefinition type 'cron' on submit.
 */
export const UI_SCHEDULE_TYPE_CRON = 5;

/**
 * UI-only schedule type for the engine's plain 'interval' recurrence (every N minutes/hours,
 * no window) - the type every migrated background-algorithm schedule uses. Not a nomenclature
 * value.
 */
export const UI_SCHEDULE_TYPE_INTERVAL = 6;

export type ScheduleEditorMode = 'engine' | 'export';

export interface ScheduleFormPropertiesOptions {
  translate: TranslateService;
  formService: FormsService;
  nomService: NomenclatureService;
  utilsService: UtilsService;
  mode: ScheduleEditorMode;
}

/**
 * The standalone schedule form: the recurrence fields extracted from the export-settings
 * dynamic form (control names kept identical so the export panel can adopt this component
 * later without data migration), plus the engine-only controls (cron expression, misfire
 * policy, overlap) shown in 'engine' mode.
 *
 * Coverage-period fields are deliberately NOT here - they describe the export content window,
 * not the recurrence, and stay with the export form until the export migration ticket.
 */
export function buildScheduleEditorProperties(
  options: ScheduleFormPropertiesOptions,
): FormProperty[] {
  const { translate, formService, nomService, utilsService, mode } = options;
  const isEngineMode = mode === 'engine';

  const isTimeOfDayScheduleType = (formGroup: FormGroup): boolean => {
    const scheduleTypeId = formGroup?.get(['schedule_type_id'])?.value;

    return (
      scheduleTypeId == NomScheduleTypes.DAILY ||
      scheduleTypeId == NomScheduleTypes.WEEKLY ||
      scheduleTypeId == NomScheduleTypes.MONTHLY
    );
  };

  const isHourly = (formGroup: FormGroup): boolean =>
    formGroup?.get(['schedule_type_id'])?.value == NomScheduleTypes.HOURLY;

  const isCron = (formGroup: FormGroup): boolean =>
    formGroup?.get(['schedule_type_id'])?.value == UI_SCHEDULE_TYPE_CRON;

  const isInterval = (formGroup: FormGroup): boolean =>
    formGroup?.get(['schedule_type_id'])?.value == UI_SCHEDULE_TYPE_INTERVAL;

  const isPeriodType = (formGroup: FormGroup): boolean =>
    isHourly(formGroup) || isInterval(formGroup);

  const dayOfMonthOptions = () => {
    const days = Array.from({ length: 31 }, (_, index) => ({
      value: index + 1,
      name: `${index + 1}`,
    }));

    return [
      ...days,
      {
        value: SCHEDULE_DAY_OF_MONTH_LAST,
        name: translate.instant(
          'schedule-editor.form-option.last-day-of-month',
        ),
      },
    ];
  };

  const scheduleTypeButtons = [
    {
      label: translate.instant('schedule-editor.button.hourly'),
      value: NomScheduleTypes.HOURLY,
      icon: ['fas', 'clock'],
    },
    {
      label: translate.instant('schedule-editor.button.daily'),
      value: NomScheduleTypes.DAILY,
      icon: ['fas', 'calendar-day'],
    },
    {
      label: translate.instant('schedule-editor.button.weekly'),
      value: NomScheduleTypes.WEEKLY,
      icon: ['fas', 'calendar-week'],
    },
    {
      label: translate.instant('schedule-editor.button.monthly'),
      value: NomScheduleTypes.MONTHLY,
      icon: ['fas', 'calendar-days'],
    },
    ...(isEngineMode
      ? [
          {
            label: translate.instant('schedule-editor.button.interval'),
            value: UI_SCHEDULE_TYPE_INTERVAL,
            icon: ['fas', 'repeat'],
          },
          {
            label: translate.instant('schedule-editor.button.cron'),
            value: UI_SCHEDULE_TYPE_CRON,
            icon: ['fas', 'terminal'],
          },
        ]
      : []),
  ];

  return [
    {
      propertyGroup: {
        key: 'schedule-editor.group.schedule-type',
        style: 'schedule-type',
      },
      propertyName: 'schedule_type_id',
      propertyLabel: translate.instant('schedule-editor.label.schedule-type'),
      controlType: 'toggle-button-group',
      formChanger: true,
      defaultValue: NomScheduleTypes.DAILY,
      additionalConfig: {
        buttons: scheduleTypeButtons,
      },
      validateBy: () => [Validators.required],
    },

    {
      propertyGroup: {
        key: 'schedule-editor.group.scheduling-options',
        style: 'scheduling-options',
      },
      propertyName: 'timezone_id',
      propertyLabel: translate.instant(
        'schedule-editor.label.schedule-timezone',
      ),
      controlType: 'select',
      // Required unless the recurrence is a plain interval; clearable so the user can return to "none"
      additionalConfig: {
        placeholder: translate.instant(
          'schedule-editor.select.select-timezone',
        ),
        clearable: true,
      },
      valueOptionsAsync: nomService.fetchTimezones().pipe(
        map((timezones) =>
          formService.transformForSelect(
            timezones,
            (timezone) => timezone.id,
            (timezone) => timezone.name,
            null,
          ),
        ),
      ),
      // Plain interval recurrence is timezone-independent - the engine ignores it there.
      shouldDisplay: (formGroup) => !isInterval(formGroup),
      validateBy: (formGroup) =>
        isInterval(formGroup) ? [] : [Validators.required],
    },

    {
      propertyGroup: {
        key: 'schedule-editor.group.scheduling-options',
        style: 'scheduling-options',
      },
      propertyName: 'schedule_time_of_day_to_send',
      propertyLabel: translate.instant(
        'schedule-editor.label.time-of-day-to-send',
      ),
      controlType: 'time-picker',
      defaultValue: utilsService.timeStringToDate('00:00'),
      shouldDisplay: isTimeOfDayScheduleType,
    },

    {
      propertyGroup: {
        key: 'schedule-editor.group.scheduling-options',
        style: 'scheduling-options',
      },
      propertyName: 'recurring_period',
      propertyLabel: translate.instant('schedule-editor.label.send-on-every'),
      controlType: 'input-number',
      defaultValue: 1,
      additionalConfig: {
        min: 1,
        max: 10080,
      },
      shouldDisplay: isPeriodType,
      validateBy: (formGroup) =>
        isPeriodType(formGroup) ? [Validators.required] : [],
    },

    {
      propertyGroup: {
        key: 'schedule-editor.group.scheduling-options',
        style: 'scheduling-options',
      },
      propertyName: 'recurring_period_unit',
      propertyLabel: translate.instant(
        'schedule-editor.label.recurring-period-type',
      ),
      controlType: 'select',
      defaultValue: 'hours',
      valueOptions: [
        {
          value: 'minutes',
          name: translate.instant('schedule-editor.form-option.minutes'),
        },
        {
          value: 'hours',
          name: translate.instant('schedule-editor.form-option.hours'),
        },
      ],
      shouldDisplay: isPeriodType,
      validateBy: (formGroup) =>
        isPeriodType(formGroup) ? [Validators.required] : [],
    },

    {
      propertyGroup: {
        key: 'schedule-editor.group.scheduling-options',
        style: 'scheduling-options',
      },
      propertyName: 'recurring_period_start_time',
      propertyLabel: translate.instant('schedule-editor.label.start-hour'),
      controlType: 'time-picker',
      defaultValue: utilsService.timeStringToDate('00:00'),
      shouldDisplay: isHourly,
      validateBy: (formGroup) =>
        isHourly(formGroup) ? [Validators.required] : [],
    },

    {
      propertyGroup: {
        key: 'schedule-editor.group.scheduling-options',
        style: 'scheduling-options',
      },
      propertyName: 'recurring_period_end_time',
      propertyLabel: translate.instant('schedule-editor.label.end-hour'),
      controlType: 'time-picker',
      defaultValue: utilsService.timeStringToDate('23:59'),
      shouldDisplay: isHourly,
      validateBy: (formGroup) =>
        isHourly(formGroup) ? [Validators.required] : [],
    },

    {
      propertyGroup: {
        key: 'schedule-editor.group.scheduling-options',
        style: 'scheduling-options',
      },
      propertyName: 'cron_expression',
      propertyLabel: translate.instant('schedule-editor.label.cron-expression'),
      controlType: 'text',
      additionalConfig: {
        placeholder: '*/15 * * * *',
      },
      shouldDisplay: (formGroup) => isEngineMode && isCron(formGroup),
      validateBy: (formGroup) =>
        isEngineMode && isCron(formGroup)
          ? [
              Validators.required,
              Validators.pattern(/^\S+\s+\S+\s+\S+\s+\S+\s+\S+$/),
            ]
          : [],
    },

    {
      propertyGroup: {
        key: 'schedule-editor.group.scheduling-options',
        style: 'scheduling-options',
      },
      propertyName: 'schedule_start_date',
      propertyLabel: translate.instant('schedule-editor.label.start-date'),
      controlType: 'date-picker',
      // Engine schedules treat a missing start date as "effective now" - forcing one when
      // editing an existing schedule would silently alter its recurrence.
      validateBy: () => (isEngineMode ? [] : [Validators.required]),
      additionalConfig: {
        // Midnight, not `new Date()`: the picker disables a day when
        // startOfDay(cell) < minDate compared by raw timestamp, so a minDate carrying a
        // time of day makes today unselectable.
        minDate: startOfDay(new Date()),
      },
    },

    {
      propertyGroup: {
        key: 'schedule-editor.group.scheduling-options',
        style: 'scheduling-options',
      },
      propertyName: 'schedule_end_date',
      propertyLabel: translate.instant('schedule-editor.label.end-date'),
      controlType: 'date-picker',
      additionalConfig: {
        placeholder: translate.instant('schedule-editor.placeholder.end-date'),
      },
    },

    {
      propertyGroup: {
        key: 'schedule-editor.group.scheduling-options',
        style: 'scheduling-options',
      },
      propertyName: 'schedule_weekdays_to_send',
      propertyLabel: translate.instant('schedule-editor.label.send-on'),
      controlType: 'checkbox-array',
      additionalConfig: {
        values: formService.weekDays,
        enableSelectAll: true,
      },
      // Only WEEKLY and HOURLY let the user narrow which weekdays fire. DAILY means every
      // day and MONTHLY is driven by day of month.
      shouldDisplay: (formGroup) => {
        const scheduleTypeId = formGroup?.get(['schedule_type_id'])?.value;

        return (
          scheduleTypeId == NomScheduleTypes.WEEKLY ||
          scheduleTypeId == NomScheduleTypes.HOURLY
        );
      },
      validateBy: (formGroup) => {
        const scheduleTypeId = formGroup?.get(['schedule_type_id'])?.value;

        return scheduleTypeId == NomScheduleTypes.WEEKLY ||
          scheduleTypeId == NomScheduleTypes.HOURLY
          ? [Validators.required, minArrayLength(1)]
          : [];
      },
    },

    {
      propertyGroup: {
        key: 'schedule-editor.group.scheduling-options',
        style: 'scheduling-options',
      },
      propertyName: 'schedule_day_of_month_to_send',
      propertyLabel: translate.instant(
        'schedule-editor.label.day-of-month-to-send',
      ),
      controlType: 'select',
      defaultValue: 1,
      valueOptions: dayOfMonthOptions(),
      shouldDisplay: (formGroup) =>
        formGroup?.get(['schedule_type_id'])?.value == NomScheduleTypes.MONTHLY,
      validateBy: (formGroup) =>
        formGroup?.get(['schedule_type_id'])?.value == NomScheduleTypes.MONTHLY
          ? [Validators.required]
          : [],
    },

    {
      propertyGroup: {
        key: 'schedule-editor.group.engine-options',
        style: 'engine-options',
      },
      propertyName: 'misfire_policy_id',
      propertyLabel: translate.instant('schedule-editor.label.misfire-policy'),
      controlType: 'select',
      defaultValue: NomScheduleMisfirePolicy.FIRE_ONCE,
      valueOptions: [
        {
          value: NomScheduleMisfirePolicy.FIRE_ONCE,
          name: translate.instant(
            'schedule-editor.form-option.misfire-fire-once',
          ),
        },
        {
          value: NomScheduleMisfirePolicy.SKIP,
          name: translate.instant('schedule-editor.form-option.misfire-skip'),
        },
      ],
      shouldDisplay: () => isEngineMode,
    },

    {
      propertyGroup: {
        key: 'schedule-editor.group.engine-options',
        style: 'engine-options',
      },
      propertyName: 'allow_overlap',
      propertyLabel: translate.instant('schedule-editor.label.allow-overlap'),
      controlType: 'toggle',
      defaultValue: false,
      shouldDisplay: () => isEngineMode,
    },
  ];
}
